import {
  Machine,
  MachineConfig,
  MachineOptions,
  spawn,
  assign,
  send,
} from 'xstate';
import convertScreenshotsToLightboxImages from 'src/utils/convertScreenshotsToLightboxImages';
import { GetScreenshotsDocument } from 'src/generated/graphql';
import gqlClient from 'src/apolloClient';
import uuid from 'uuid/v4';
import utils from 'src/utils';
import EventNames from 'src/lib/eventNames';
import { generateScreenshotUploadMachine } from './screenshotUploadMachine';
import notificationMachine from './notificationMachine';
import { generateLightboxMachine } from './lightboxMachine';
import { generateGalleryMachine } from './galleryMachine';

const {
  CLICK_NAVIGATE_IN_LIGHTBOX_MODE,
  CLICK_TAKE_SCREENSHOT,
  CLICK_GALLERY_IMAGE,
  DOWNLOAD_FROM_LIGHTBOX,
} = EventNames.viewer;
const { trackMetric } = utils.metrics;

interface ScreenshotMachineSchema {
  states: {
    viewfinder: {
      states: {
        idle: {};
        flash: {};
      };
    };
    lightbox: {};
  };
}

export interface ScreenshotMachineContext {
  jobId: number;
  lightboxRef?: any;
  gallerySidebarRef?: any;
  notifierMachineRef?: any;
  captureScreenshotRefs?: any;
  captureIsInProgress?: boolean;
  metricData: any;
}

type ScreenshotMachineEvent =
  | { type: 'TAKE_SCREENSHOT' }
  | { type: 'SHOW_LIGHTBOX'; id: string; isEnhanced: boolean }
  | { type: 'LIGHTBOX_NAVIGATION' }
  | { type: 'LIGHTBOX_DOWNLOAD'; isEnhanced: boolean }
  | { type: 'EXIT_LIGHTBOX' }
  | { type: 'SHOW_MESSAGE' }
  | { type: 'CAPTURE_SUCCESS'; id: string }
  | { type: 'CAPTURE_FAIL'; id: string };

export const config: MachineConfig<
  ScreenshotMachineContext,
  ScreenshotMachineSchema,
  ScreenshotMachineEvent
> = {
  id: 'screenshot',
  initial: 'viewfinder',
  context: {
    jobId: 0,
    captureIsInProgress: false,
    metricData: {},
  },
  entry: 'assignEntryRefs',
  states: {
    viewfinder: {
      initial: 'idle',
      on: {
        TAKE_SCREENSHOT: {
          target: '.flash',
          actions: [
            'setInProgress',
            'assignCaptureRef',
            'fireTakeScreenshotMetric',
          ],
        },
        CAPTURE_SUCCESS: {
          actions: 'checkInProgress',
        },
        CAPTURE_FAIL: {
          actions: ['checkInProgress', 'sendNotificationMessage'],
        },
        SHOW_LIGHTBOX: {
          target: 'lightbox',
          actions: 'fireGalleryItemClickMetric',
        },
      },
      states: {
        idle: {},
        flash: {
          after: {
            10: 'idle',
          },
        },
      },
    },
    lightbox: {
      entry: 'prepareLightboxData',
      on: {
        EXIT_LIGHTBOX: {
          target: 'viewfinder',
        },
        LIGHTBOX_NAVIGATION: {
          actions: 'fireLightboxNavigateMetric',
        },
        LIGHTBOX_DOWNLOAD: {
          actions: 'fireLightboxDownloadMetric',
        },
      },
    },
  },
};

export const options: Partial<MachineOptions<
  ScreenshotMachineContext,
  ScreenshotMachineEvent
>> = {
  actions: {
    sendNotificationMessage: send(
      (_, event: any) => {
        const { type, ...data } = event;
        return {
          type: 'SHOW_MESSAGE',
          data,
        };
      },
      { to: 'notifierMachineRef' },
    ),
    setInProgress: assign((_) => ({ captureIsInProgress: true })),
    assignCaptureRef: assign((context) => {
      const id = uuid();
      return {
        captureScreenshotRefs: {
          ...context.captureScreenshotRefs,
          [id]: {
            ref: spawn(
              generateScreenshotUploadMachine(context.jobId, id),
              `capture-${id}`,
            ),
            done: false,
          },
        },
      };
    }),
    assignEntryRefs: assign((context: ScreenshotMachineContext) => ({
      gallerySidebarRef: spawn(
        generateGalleryMachine(context.jobId),
        'gallerySidebarRef',
      ),
      notifierMachineRef: spawn(notificationMachine, 'notifierMachineRef'),
    })),
    prepareLightboxData: assign({
      lightboxRef: (context, event: any) => {
        // TODO: add `getLightboxScreenshots` query on the server to avoid FE
        // formatting the data and reduce bug surface
        const data: any = gqlClient.readQuery({
          query: GetScreenshotsDocument,
          variables: { jobId: context.jobId },
        });
        const lightboxContext = convertScreenshotsToLightboxImages(
          data.screenshots,
          event.id,
        );
        const lightboxMachine = generateLightboxMachine(lightboxContext);
        return spawn(lightboxMachine, 'lightboxRef');
      },
    }),
    checkInProgress: assign((context, event: any) => {
      const cameras = context.captureScreenshotRefs;
      cameras[event.id].done = true;
      if (event.error) {
        cameras[event.id].error = event.error;
      }
      return {
        captureScreenshotRefs: cameras,
        captureIsInProgress: (Object.values(cameras) as any).some(
          (cam: any) => !cam.done,
        ),
      };
    }),
    fireTakeScreenshotMetric: (context) => {
      trackMetric(CLICK_TAKE_SCREENSHOT, context.metricData);
    },
    fireLightboxNavigateMetric: (context) => {
      trackMetric(CLICK_NAVIGATE_IN_LIGHTBOX_MODE, context.metricData);
    },
    fireLightboxDownloadMetric: (context, event: any) => {
      const { isEnhanced } = event;
      const payload = {
        ...context.metricData,
        image_type: isEnhanced ? 'enhanced' : 'standard',
      };
      trackMetric(DOWNLOAD_FROM_LIGHTBOX, payload);
    },
    fireGalleryItemClickMetric: (context, event: any) => {
      const { isEnhanced } = event;
      const payload = {
        ...context.metricData,
        image_type: isEnhanced ? 'enhanced' : 'standard',
      };
      trackMetric(CLICK_GALLERY_IMAGE, payload);
    },
  },
};

const screenshotMachine = Machine<
  ScreenshotMachineContext,
  ScreenshotMachineSchema,
  ScreenshotMachineEvent
>(config, options);

export const generateScreenshotMachine = (jobId: number, metricData: any) => {
  return screenshotMachine.withContext({ jobId, metricData });
};

export default screenshotMachine;
